import { zodResolver } from "@hookform/resolvers/zod";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle, FormHelperText,
  IconButton,
  Table, TableBody, TableCell,
  TableHead, TableRow,
  TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm, useWatch } from "react-hook-form";
import { AutocompleteElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";



import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { AiconGameGroup } from "@/module/service-configs-module/aicon-game-group-module/aicon-game-group-table";
import CustomSelectOption from "@/components/custom-select-option";
import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";


export interface Perm {
  room_id: string,
  partner_room_id: string,
  ai_room_id: string,
  game_types: string[]
}

const defaultInitialPerm: Perm = {
  room_id: "",
  partner_room_id: "",
  ai_room_id: "",
  game_types: []
};

const defaultInitialData = {
  name: "",
  auth_groups: new Set,
  enabled: true,
  data: {
    tg_link: "",
    expired_at: undefined,
    ai_subject: "",
    token: "",
    perms: [],
  },

}


export function AiconGameGroupCreateEditModal({
  mode = "add",
  initialData,
  onSave,
  onCancel,
}:
{
  mode?: "add" | "edit";
  initialData?: AiconGameGroup;
  onSave?: (data: AiconGameGroup) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();


  const schema = z.object({
    name: z.string().min(1, "Name is required"),
    enabled: z.boolean().default(false),
    auth_groups: z.preprocess(
      (value) => {
        if (Array.isArray(value)) {
          return new Set(value);
        }
        return value;
      },
      z.set(z.string()).nonempty("Select at least one group")
    ),
    data: z.object({
      perms: z.array(z.object({
        room_id: z.string(),
        partner_room_id: z.string(),
        ai_room_id: z.string(),
        game_types: z.array(z.string())
      })),
      tg_link: z.string().optional(),
      expired_at: z.number().optional(), //???
      ai_subject: z.string(),
      token: z.string().transform((val) => {
        return val || generateFixedBase64Token();
      })
    }),
  });

  const [editingPerm, setEditingPerm] = useState<Perm | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [permModalOpen, setPermModalOpen] = useState(false);


  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery();
  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs?.data]);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors , isDirty},
  } = useForm<AiconGameGroup>({
    resolver: zodResolver(schema),
    defaultValues: initialData ?? defaultInitialData
  });

  const perms = useWatch({ control, name: "data.perms" });

  const handlePermSave = (perm: Perm) => {


    const perms = getValues("data.perms") ?? [];
    if (editIndex != null) {
      perms[editIndex] = perm;
    } else {
      perms.push(perm);
    }
    setValue("data.perms", perms);
    setPermModalOpen(false);
    setEditingPerm(null);
    setEditIndex(null);
  };

  const handlePermDelete = (index: number, room_id: string) => {
    setValue("data.perms", [
      ...getValues("data.perms").filter((_, i) => i !== index /*&& room_id !== _.room_id*/),
    ]);
  };

  const onSubmit = (data: AiconGameGroup) => {

    onSave?.(data);
    reset();
    onCancel && onCancel();
  };

  function generateFixedBase64Token(): string {
    const byteLength = 48; // 48 байт → 64 символа
    const array = new Uint8Array(byteLength);
    crypto.getRandomValues(array);

    const base64 = btoa(String.fromCharCode(...array));
    return base64
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "") // убираем padding, если он есть
      .padEnd(64, "A"); // на всякий случай добиваем до 64
  }

  return (
    <>
      <Dialog open={mode === "edit" || mode === "add"}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' && isDirty && !window.confirm("Data has been changed. Do you want discard changes?")) return; //ask question if changes==true and click outSide
                onCancel?.();
              }}
      >
        <DialogTitle>
          {mode === "add" ? t("add") : t("edit")}
          <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}
          >
            <TextFieldElement
              name="name"
              control={control}
              label={t("name")}
              placeholder={t("name")}
              fullWidth
              error={!!errors.name?.message}
              helperText={errors.name?.message}
              slotProps={{
                input: {
                  disabled: mode === "edit",
                },
              }}
            />

            <AutocompleteElement
              name="auth_groups"
              loading={authGroupsIsLoading}
              control={control}
              label={t("authGroups")}
              /*required*/
              options={authGroupsOptions.map((it) => it)}
              multiple
              textFieldProps={{
                error: !!errors.auth_groups,
                helperText: errors.auth_groups?.message ?? " ",
                variant: "standard",
              }}
            />


            <TextFieldElement
              name="data.tg_link"
              control={control}
              label={t("tgLink")}
              placeholder={t("tgLink")}
              fullWidth
              error={!!errors.data?.tg_link?.message}
              helperText={errors.data?.tg_link?.message}
            />

            <Controller
              name="data.expired_at"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DateTimePicker
                  label={t("expired_at")}
                  value={field.value ? dayjs(field.value) : null}
                  minDateTime={dayjs()}
                  onChange={(val) => {
                    const millis = val ? val.valueOf() : null; //МИЛЛИСЕКУНДЫ
                    field.onChange(millis);
                  }}
                  slotProps={{
                    textField: {
                      error: !!error?.message,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <TextFieldElement
              name="data.ai_subject"
              control={control}
              label={t("aiSubject")}
              placeholder={t("aiSubject")}
              fullWidth
              error={!!errors.data?.ai_subject?.message}
              helperText={errors.data?.ai_subject?.message}
            />



            {/* --- PERMS TABLE --- */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3>{t("permissions")}</h3>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingPerm(defaultInitialPerm);
                    setPermModalOpen(true);
                  }}
                >
                  {t("add") + " " + t("permissions")}
                </Button>
              </div>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "20%" }}>{t("roomId")}</TableCell>
                    <TableCell sx={{ width: "20%" }}>{t("partnerRoomId")}</TableCell>
                    <TableCell sx={{ width: "20%" }}>{t("aiRoomId")}</TableCell>
                    <TableCell sx={{ width: "30%" }}>{t("gameTypes")}</TableCell>
                    <TableCell sx={{ width: "10%" }} align="right">
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {perms?.map((perm, index) => (
                    <TableRow key={index}>
                      <TableCell>{perm.room_id}</TableCell>
                      <TableCell>{perm.partner_room_id}</TableCell>
                      <TableCell>{perm.ai_room_id}</TableCell>
                      <TableCell>
                        {perm.game_types && perm.game_types.length > 0
                          ? perm.game_types.join(", ")
                          : "-"
                        }
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingPerm(perm);
                            setEditIndex(index);
                            setPermModalOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handlePermDelete(index, perm.room_id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!!errors.data?.perms && (
                <FormHelperText error>{errors.data?.perms?.message as string}</FormHelperText>
              )}
            </div>



            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={onCancel}>{t("cancel")}</Button>
              <Button variant="contained" type="submit">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PermModal
        open={permModalOpen}
        initialData={editingPerm}
        onSave={handlePermSave}
        onCancel={() => {
          setPermModalOpen(false);
          setEditingPerm(null);
          setEditIndex(null);
        }}
      />
    </>
  );
}

const PermModal = ({
                     open,
                     initialData,
                     onSave,
                     onCancel,
                   }: {
  open: boolean;
  initialData: Perm | null;
  onSave: (club: Perm) => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();

  const { data: gameTypesRs, isLoading: gameTypesAreLoading } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.GAME_TYPE,
  });

  const gameTypeOptions = useMemo(() => {
    return (
      gameTypesRs?.data.content
        ?.filter((item) => item.enabled)
        .map((item) => ({ value: item.name, label: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [gameTypesRs]);

  const permSchema = z.object({
    room_id: z.string(),
    partner_room_id: z.string(),
    ai_room_id: z.string(),
    game_types: z.array(z.string())
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Perm>({
    resolver: zodResolver(permSchema), // Нужно будет создать соответствующую схему
    defaultValues: initialData ?? defaultInitialPerm,
  });

  useEffect(() => {
    if (open && initialData) reset(initialData);
  }, [initialData, open, reset]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? t("add") : t("edit")}
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit((data) => onSave(data))}
          style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}
        >
          <TextFieldElement
            name="room_id"
            control={control}
            label={t("roomId")}
            placeholder={t("roomId")}
            fullWidth
          />

          <TextFieldElement
            name="partner_room_id"
            control={control}
            label={t("partnerRoomId")}
            placeholder={t("partnerRoomId")}
            fullWidth
          />

          <TextFieldElement
            name="ai_room_id"
            control={control}
            label={t("aiRoomId")}
            placeholder={t("aiRoomId")}
            fullWidth
          />

          <AutocompleteElement
            name="game_types"
            loading={gameTypesAreLoading}
            control={control}
            label={t("gameTypes")}
            options={gameTypeOptions.map(v => v.value)}
            multiple
            textFieldProps={{
              error: !!errors.game_types,
              helperText: errors.game_types?.message ?? " ",
              variant: "standard",
            }}
            autocompleteProps={{
              renderOption: (props, option, { selected }) => (
                <CustomSelectOption label={option} selected={selected} props={props} />
              ),
            }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onCancel}>{t("cancel")}</Button>
            <Button variant="contained" type="submit">
              {t("save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
